/**
 * The email template editor's draft, and the two requests built from it — the
 * preview and the save.
 *
 * These lived inside `routes/templates/TemplateEditorPage.tsx` until slice 56,
 * and they moved for one reason: **so a verify suite can run them.** The page
 * imports `lib/api/client`, which imports `lib/config.ts`, which reads
 * `import.meta.env` and evaluates only inside Vite — so nothing in that file
 * can be called from a `tsx` suite, and "the width and background reach the
 * save and the preview requests" could only be asserted by reading the page's
 * source for a string. A string in a file is not a request. Here, the suite
 * builds a draft, calls {@link previewCandidateOf} and {@link saveRequestOf},
 * and parses what comes back with the real contract.
 *
 * Nothing here fetches, and nothing here decides a rule: the contract parses
 * both requests, in the page, before either is sent. What this file owns is
 * the *shape of the editor's state* and which of its fields go where.
 *
 * ## The draft
 *
 * The body and the footer are held apart. That is not a stylistic choice: it
 * means "delete the footer" and "move a block below the footer" are not
 * operations the editor refuses, they are operations it cannot express. The
 * page's header argues it at length.
 *
 * The empty string stands for "not set" in the three optional lines, because
 * that is what an input holds when it is cleared; the shared write schemas
 * transform `''` to `null` on the way out, so the two never disagree about which
 * one means absent.
 *
 * `bodyWidth` and `background` (slice 56) are the template's own values,
 * copied in unchanged. A template nobody touches sends back exactly what it
 * arrived with, which is how the API's "standard and white compile to today's
 * bytes" reaches the wire from this side.
 */
import {
  documentBody,
  documentFooter,
  type BodyBlock,
  type FooterBlock,
  type Template,
  type TemplateBackground,
  type TemplateBodyWidth,
  type TemplateDocument,
} from '@wend/shared';

export interface EmailTemplateDraft {
  name: string;
  subject: string;
  preheader: string;
  replyTo: string;
  bodyWidth: TemplateBodyWidth;
  background: TemplateBackground;
  body: BodyBlock[];
  footer: FooterBlock;
}

export function draftOf(template: Template): EmailTemplateDraft {
  // `blocks` became nullable in slice 7: a WhatsApp template has no email
  // document. This editor is the email one, and `templates_email_has_blocks`
  // means an email template always has one — so a null here is a WhatsApp
  // template routed to the wrong screen, which throws rather than rendering an
  // empty editor over somebody's message.
  const blocks = template.blocks;
  if (blocks === null) {
    throw new Error('This is not an email template, so it has no block document to edit.');
  }
  return {
    name: template.name,
    subject: template.subject ?? '',
    preheader: template.preheader ?? '',
    replyTo: template.replyTo ?? '',
    bodyWidth: template.bodyWidth,
    background: template.background,
    body: documentBody(blocks),
    footer: documentFooter(blocks),
  };
}

/**
 * The two halves back together.
 *
 * The assertion is the same one `templateDocumentSchema` makes for its own
 * transform, and it is sound for the same reason: a spread of body blocks
 * followed by exactly one footer *is* the tuple, and TypeScript cannot follow
 * that from the spread alone.
 */
export function documentOf(draft: EmailTemplateDraft): TemplateDocument {
  return [...draft.body, draft.footer] as TemplateDocument;
}

/** What is sent to the preview endpoint, before the contract has seen it. */
export interface PreviewCandidate {
  blocks: TemplateDocument;
  subject: string;
  preheader: string;
  bodyWidth: TemplateBodyWidth;
  background: TemplateBackground;
  substituteSampleValues: boolean;
}

export function previewCandidateOf(draft: EmailTemplateDraft, substitute: boolean): PreviewCandidate {
  return {
    blocks: documentOf(draft),
    subject: draft.subject,
    preheader: draft.preheader,
    // The unsaved choice, so the frame shows it before anything is stored.
    bodyWidth: draft.bodyWidth,
    background: draft.background,
    substituteSampleValues: substitute,
  };
}

/** What is sent to the save endpoint, before the contract has seen it. */
export interface SaveCandidate {
  name: string;
  subject: string;
  preheader: string;
  replyTo: string;
  bodyWidth: TemplateBodyWidth;
  background: TemplateBackground;
  blocks: TemplateDocument;
}

/**
 * The whole draft, every time — `blocks` is a replacement, not a patch, and the
 * two settings ride with it, so a save recompiles `html_compiled` against the
 * width and background on screen rather than the stored ones.
 */
export function saveRequestOf(draft: EmailTemplateDraft): SaveCandidate {
  return {
    name: draft.name,
    subject: draft.subject,
    preheader: draft.preheader,
    replyTo: draft.replyTo,
    bodyWidth: draft.bodyWidth,
    background: draft.background,
    blocks: documentOf(draft),
  };
}

/**
 * A cheap identity for a draft or a request.
 *
 * Used for two things: deciding whether there are unsaved changes, and deciding
 * whether a preview request would ask the same question as the last one. Both
 * want "is this the same document", and on a hundred blocks of short text this
 * costs less than the render that follows it.
 *
 * **Keys are sorted (slice 56).** A formatted paragraph arrives from the API
 * with `runs` wherever the API's schema puts it and leaves the paragraph editor
 * with `runs` wherever `withRuns` puts it; plain `JSON.stringify` called two
 * identical paragraphs different and would have left "unsaved changes" lit on a
 * template nobody changed. Arrays keep their order: the order of blocks and of
 * runs *is* the document.
 */
export function signature(value: unknown): string {
  return JSON.stringify(value, (_key, inner: unknown) =>
    inner !== null && typeof inner === 'object' && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
        )
      : inner,
  );
}
